import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Pagination,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms';

import { useSlideActions } from '../../../hooks/useSlideActions';
import { useSlideCategories } from '../../../hooks/useSlideCategories';
import type { RequiredFieldsToCreateSlide } from '../../../hooks/useSlideLibrary';
import { useSlideLibrary } from '../../../hooks/useSlideLibrary';
import { useSlideLibraryQuery } from '../../../hooks/useSlideLibraryQuery';
import { useSlideStatuses } from '../../../hooks/useSlideStatuses';
import { slideEditorParamsAtom } from '../../../store/atoms/slideEditorParamsAtom';
import { LibrarySearchBar } from '../../molecules/LibrarySearchBar';
import { SlideCard } from '../../molecules/SlideCard';
import { CreateSlideModal } from '../../organisms/CreateSlideModal';
import { DeleteSlideConfirmModal } from '../../organisms/DeleteSlideConfirmModal';
import { LibraryEmptyState } from '../../organisms/LibraryEmptyState';

/**
 * Template da biblioteca de slides.
 * Busca os cards via `useGetApiV1Ssoimage` (TanStack Query) e exibe um grid flat
 * com busca, criação, alteração de status e exclusão de slides.
 */
export function SlideLibraryTemplate() {
  const { t } = useTranslation('slideEditor');
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingDeleteSlideId, setPendingDeleteSlideId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const setEditorParams = useSetAtom(slideEditorParamsAtom);
  const { categories } = useSlideCategories();

  // Dados do grid via API (TanStack Query)
  const { allSlides, hasResults, isLoading, totalPages } = useSlideLibraryQuery({
    pageNumber: currentPage,
  });

  // Filtros e criação de slides (estado local)
  const { filters, setSearchQuery, setCategoryFilter, applySearch, createSlide, isSaving } =
    useSlideLibrary();

  const { getStatusLabel, getTagStatus } = useSlideStatuses();

  const { changeStatus, confirmDelete, isUpdatingStatus, isDeleting } = useSlideActions({
    onDeleted: () => setPendingDeleteSlideId(null),
  });

  const handleCreateSlide = async ({
    title,
    categoryId,
    dateEnd,
    dateStart,
  }: RequiredFieldsToCreateSlide) => {
    const newSlide = await createSlide({ title, categoryId, dateEnd, dateStart });
    setShowCreateModal(false);
    setEditorParams({ ...(newSlide.id !== undefined ? { slideId: newSlide.id } : {}), categoryId });
    void navigate({ to: '/slide-editor', search: { slideId: newSlide.id } });
  };

  const handleOpenSlide = (slideId: number, categoryId: number) => {
    setEditorParams({ slideId, categoryId });
    void navigate({ to: '/slide-editor', search: { slideId } });
  };

  const getCategoryName = (categoryId: number): string =>
    categories.find(c => c.id === Number(categoryId))?.name ?? '';

  const pendingDeleteSlide =
    pendingDeleteSlideId !== null ? allSlides.find(s => s.id === pendingDeleteSlideId) : undefined;

  const handleDeleteConfirm = () => {
    if (pendingDeleteSlide?.id !== undefined) {
      confirmDelete(pendingDeleteSlide.id);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden bg-ctx-layout-body">
      {/* Page header */}
      <PageHeader
        title={t('library.title')}
        subtitle={t('library.subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('library.title')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={
          <Button
            hierarchy="primary"
            size="md"
            leadingIcon={PlusIcon}
            onClick={() => setShowCreateModal(true)}
          >
            {t('library.createSlide')}
          </Button>
        }
      />

      {/* Search bar */}
      <div className="mt-xxs shrink-0 rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
        <LibrarySearchBar
          searchQuery={filters.searchQuery}
          selectedCategoryId={filters.appliedCategoryId}
          categories={categories}
          onSearchQueryChange={setSearchQuery}
          onCategoryChange={id => {
            setCategoryFilter(id);
            setCurrentPage(1);
            applySearch();
          }}
          onSearch={() => {
            setCurrentPage(1);
            applySearch();
          }}
        />
      </div>

      {/* Slides grid */}
      <main className="flex flex-col gap-xxs py-xxs">
        {isLoading ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-xxs">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-nano shadow-level1"
              >
                <Skeleton className="w-full rounded-sm" style={{ aspectRatio: '16 / 9' }} />
                <div className="flex flex-col gap-quark px-quark pb-quark">
                  <Skeleton className="h-mini w-3/4" />
                  <Skeleton className="h-xs w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : hasResults ? (
          <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-xxs">
            {allSlides.map(slide => (
              <SlideCard
                key={slide.id}
                slide={slide}
                categoryName={getCategoryName(slide.categoryId)}
                onClick={() => handleOpenSlide(slide.id, slide.categoryId)}
                onStatusChange={(newStatus: 1 | 2) => changeStatus(slide.id, newStatus)}
                onDelete={() => {
                  if (slide.id !== undefined) setPendingDeleteSlideId(slide.id);
                }}
                {...(slide.statusId !== undefined
                  ? {
                      statusLabel: getStatusLabel(slide.statusId),
                      statusTagVariant: getTagStatus(slide.statusId),
                    }
                  : {})}
                isUpdatingStatus={isUpdatingStatus}
                isDeleting={isDeleting && pendingDeleteSlideId === slide.id}
                statusTagVariant={slide.statusId === 1 ? 'helper' : 'positive'}
              />
            ))}
          </div>
        ) : (
          <LibraryEmptyState
            isFiltered={filters.appliedQuery !== '' || filters.appliedCategoryId !== null}
            onCreateCategory={() => setShowCreateModal(true)}
          />
        )}

        {/* Paginação */}
        {!isLoading && hasResults && totalPages !== undefined && totalPages > 1 && (
          <div className="flex justify-center pb-md">
            <Pagination
              pageAmount={totalPages}
              activeIndex={currentPage - 1}
              ellipsisLabel=""
              setActiveIndex={index => setCurrentPage((index as number) + 1)}
            />
          </div>
        )}
      </main>

      {/* Create slide modal */}
      {showCreateModal && (
        <CreateSlideModal
          isSaving={isSaving}
          categories={categories}
          onConfirm={handleCreateSlide}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {/* Delete confirmation modal */}
      {pendingDeleteSlideId !== null && (
        <DeleteSlideConfirmModal
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setPendingDeleteSlideId(null)}
        />
      )}
    </div>
  );
}
